import { Button, TailwindSpinner } from '@components/common';
import { Trans, useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { useSwrResumeGetPrice } from '@hooks/useSwrApi/endpoints/user';
import {
  useSwrGetSearchPackAggregatedDetails,
} from '@hooks/useSwrApi/endpoints/user-searchpack/useSwrGetSearchPackAggregatedDetails';
import { JobFolderSelector } from '@components/templates/cv/partials/JobFolderSelector';
import { OperationJob } from '@type/v1-api-types';
import { useSwrResumeGetOperationJob } from '@hooks/useSwrApi/endpoints/resume-operations';
import { SearchpackButton } from '@components/templates/my-jobs/Card/partials/SearchpackButton';

interface CustomConfirmModalContentProps {
  onSendSuccess: (value: boolean) => void;
  defaultPrice?: {
    price: number;
    fullPrice: number;
  };
  userSlug: string;
  jobSlug?: string;
  isPassiveRecommendation?: boolean;
  jobContextDetail?: {
    jobFolderId: string | number;
    title: string;
    jobSlug: string;
  };
  updateActiveJobFolderContext?: (item: OperationJob) => void;
}

export const CustomConfirmModalContent = ({
  onSendSuccess,
  defaultPrice,
  userSlug,
  jobSlug,
  isPassiveRecommendation = false,
  jobContextDetail,
  updateActiveJobFolderContext,
}: CustomConfirmModalContentProps) => {
  const { t } = useTranslation('common');

  const [activeJobContext, setActiveJobContext] = useState<OperationJob>({
    jobId: Number(jobContextDetail?.jobFolderId),
    title: jobContextDetail?.title ?? '',
    jobSlug: jobContextDetail?.jobSlug ?? '',
  });

  const { data: priceList } = useSwrResumeGetPrice({
    userSlug,
    locale: 'ro',
    shouldFetch: defaultPrice?.price === undefined,
    isPassiveRecommendation,
    jobSlug,
  });

  const activeJobSlug = activeJobContext?.jobSlug ?? '';
  const { data: aggregatedData, isLoading: isLoadingPackageDetails } = useSwrGetSearchPackAggregatedDetails({
    locale: 'ro',
    jobSlug: activeJobSlug,
    shouldFetch: activeJobSlug.length > 0,
  });

  const {
    trigger: getOperationJobTrigger,
  } = useSwrResumeGetOperationJob({
    locale: 'ro',
    successCallback: (operationJobData) => {
      setActiveJobContext(operationJobData);
    },
  });

  // Sync activeJobContext when jobContextDetail is updated asynchronously (e.g. after job info is fetched)
  useEffect(() => {
    if (jobContextDetail?.jobFolderId && Number(jobContextDetail.jobFolderId) !== 0) {
      setActiveJobContext({
        jobId: Number(jobContextDetail.jobFolderId),
        title: jobContextDetail.title ?? '',
        jobSlug: jobContextDetail.jobSlug ?? '',
      });
    }
  }, [jobContextDetail?.jobFolderId, jobContextDetail?.title, jobContextDetail?.jobSlug]);

  useEffect(() => {
    if (jobContextDetail && jobContextDetail?.jobSlug?.length === 0 && activeJobContext?.title?.length === 0) {
      void getOperationJobTrigger();
    }
  }, [jobContextDetail, getOperationJobTrigger, activeJobContext]);


  // Determine which price to use: defaultPrice takes precedence, fallback to priceList
  const priceToShow = defaultPrice ?? priceList as ApiSchema<'ResumePrice'>;

  const isPromoPrice = typeof priceToShow?.price === 'number'
    && typeof priceToShow?.fullPrice === 'number'
    && priceToShow.price < priceToShow.fullPrice;


  return (
    <>
      <div className="border-b px-4 py-2 text-left">
        <span className="block text-xl font-bold">
          {t('cv.detail.contact.type')}
        </span>
      </div>
      <div className="p-5">
        <div className="mb-5 text-center">
          <div className="mb-5 rounded-lg border border-dashed border-secondary bg-surface-100 px-3 py-2 shadow-lg">
            <JobFolderSelector
              selectedJobFolder={{
                jobId: Number(activeJobContext?.jobId) ?? 0,
                title: activeJobContext?.title ?? '',
                jobSlug: activeJobContext?.jobSlug ?? '',
              }}
              sendJobFolderInfoToParent={(item) => {
                setActiveJobContext(item);
                updateActiveJobFolderContext?.(item);
              }}
            />
          </div>

          {typeof priceToShow?.price === 'number' && priceToShow?.price > 0 && (
            <span className="text-sm text-ink-medium">{t('global.label.unlock.price.candidate.agree')}</span>
          )}

          {isLoadingPackageDetails ? (
            <div className="flex items-center justify-center">
              <TailwindSpinner className="size-5" />
              {' '}
            </div>
          ) : (
            <>
              {aggregatedData?.availableUnlocks ? (
                <div
                  className="w-full justify-center border-b py-3 pt-4 text-sm text-ink-medium"
                >
                  <Trans
                    i18nKey="confirm.modal.searchpack.available"
                    values={{ availableUnlocks: aggregatedData?.availableUnlocks, maxUnlocks: aggregatedData?.maxUnlocks }}
                  />
                </div>
              ) : (
                <div
                  className="w-full justify-center border-b py-3 pt-4 text-sm text-ink-medium"
                >
                  {`${t('cv.detail.contact.label')}:`}
                  <div className="mt-2 flex items-end justify-center gap-1">
                    {isPromoPrice && (
                      <span className="text-sm text-danger line-through">
                        {priceToShow?.fullPrice}
                      </span>
                    )}
                    <span className="text-[30px] font-bold leading-none text-secondary">
                      {priceToShow?.price}
                    </span>
                    <span className="text-base font-medium text-secondary">
                      {t('global.label.credits')}
                    </span>
                  </div>

                  {priceToShow?.price === 0 && (
                  <div className="mb-2 mt-4 text-center text-info">{t('unlock_info.recommended.free')}</div>
                  )}

                </div>
              )}
            </>
          )}


        </div>

        <div className="text-center">
          <Button
            role="presentation"
            size="sm"
            disabled={isLoadingPackageDetails}
            color="secondary"
            onClick={() => onSendSuccess(true)}
            className="mr-2"
          >
            {t('global.label.unlock.candidate.info')}
          </Button>
          <Button
            role="presentation"
            size="sm"
            color="error"
            onClick={() => onSendSuccess(false)}
          >
            {t('global.label.unlock.candidate.deny')}
          </Button>
        </div>

      </div>

    </>
  );
};
